import Footer from 'app/components/FooterComponent';
import Header from 'app/components/HeaderComponent';
import HeaderImage from 'home/components/HeaderImage';
import Loader from '__common/components/LoaderComponent';
import React from 'react';
import { Button, FormControl, Stack, InputAdornment, IconButton } from '@mui/material';
import { TextField } from '@mui/material';
import { CHANGE_PASSWORD, CHANGE_PASSWORD_CLEAR } from 'actions';
import { connect } from 'react-redux';
import config from 'config';

type Props = {
  dispatch: Function;
  changePassword: changePasswordState;
};

type State = {
  oldPassword: string;
  newPassword: string;
  newPasswordConfirmation: string;
  passwordNotMatch: boolean;
  showOldPassword: boolean;
  showNewPassword: boolean;
  showConfirmPassword: boolean;
  redirectCountdown: number;
  emptyFieldError: boolean;
};

class ChangePassword extends React.Component<Props, State> {
  state = {
    oldPassword: '',
    newPassword: '',
    newPasswordConfirmation: '',
    passwordNotMatch: false,
    showOldPassword: false,
    showNewPassword: false,
    showConfirmPassword: false,
    redirectCountdown: 0,
    emptyFieldError: false,
  };

  redirectTimer: NodeJS.Timeout | null = null;
  oldPasswordInputRef = React.createRef<HTMLInputElement>();
  newPasswordInputRef = React.createRef<HTMLInputElement>();
  confirmPasswordInputRef = React.createRef<HTMLInputElement>();

  componentDidUpdate(prevProps: Props) {
    const { changePassword: { success } } = this.props;
    const { redirectCountdown } = this.state;

    if (success && !prevProps.changePassword.success && redirectCountdown === 0) {
      this.setState({ redirectCountdown: 10 });
      this.startCountdown();
    }
  }

  startCountdown = () => {
    this.redirectTimer = setInterval(() => {
      this.setState((prevState) => {
        const newCount = prevState.redirectCountdown - 1;
        if (newCount <= 0) {
          if (this.redirectTimer) {
            clearInterval(this.redirectTimer);
          }
          window.location.href = config.loginPageRedirection;
        }
        return { redirectCountdown: newCount };
      });
    }, 1000);
  }

  componentWillUnmount() {
    const { dispatch } = this.props;
    if (this.redirectTimer) {
      clearInterval(this.redirectTimer);
    }
    dispatch(CHANGE_PASSWORD_CLEAR());
  }

  setOldPassword = (oldPassword: string) => {
    this.setState({ oldPassword });
  }

  setNewPassword = (newPassword: string) => {
    this.setState({ newPassword });
  }

  setNewPasswordConfirmation = (newPasswordConfirmation: string) => {
    this.setState({ newPasswordConfirmation });
  }

  restoreNotMatch = () => {
    const { passwordNotMatch } = this.state;

    if (passwordNotMatch) {
      this.setState({ passwordNotMatch: false });
    }
  }

  toggleOldPasswordVisibility = () => {
    this.setState({ showOldPassword: !this.state.showOldPassword }, () => {
      this.oldPasswordInputRef.current?.focus();
    });
  }

  toggleNewPasswordVisibility = () => {
    this.setState({ showNewPassword: !this.state.showNewPassword }, () => {
      this.newPasswordInputRef.current?.focus();
    });
  }

  toggleConfirmPasswordVisibility = () => {
    this.setState({ showConfirmPassword: !this.state.showConfirmPassword }, () => {
      this.confirmPasswordInputRef.current?.focus();
    });
  }

  changePassword = () => {
    const { dispatch } = this.props;
    const { oldPassword, newPassword, newPasswordConfirmation } = this.state;

    if (!oldPassword || !newPassword || !newPasswordConfirmation) {
      this.setState({ emptyFieldError: true });
      return;
    }

    this.setState({ emptyFieldError: false });

    if (newPassword !== newPasswordConfirmation) {
      this.setState({ passwordNotMatch: true });
    } else {
      dispatch(
        CHANGE_PASSWORD(oldPassword, newPassword, newPasswordConfirmation),
      );
    }
  }

  passwordNotMatch = () => {
    const { passwordNotMatch } = this.state;
    if (passwordNotMatch) {
      return (
        <>
          <div className="input-warning">Password does not match</div>
        </>
      );
    }

    return null;
  }

  emptyFieldValidation = () => {
    const { emptyFieldError } = this.state;
    if (emptyFieldError) {
      return (
        <>
          <div className="input-warning">All fields are required</div>
        </>
      );
    }

    return null;
  }

  content = () => {
    const {
      changePassword: { error, sending, success },
    } = this.props;

    const messageStyle = {
      fontFamily: 'PT Sans !important',
      fontSize: '1em !important',
      color: '#5f5f5f !important',
      padding: '20px !important',
      textAlign: 'center' as const,
    };

    if (error) {
      return (
        <>
          <div className="maintitle" style={{ fontFamily: 'Oswald !important', letterSpacing: '0.05em !important', fontSize: '1.125em !important', fontWeight: '500 !important' }}>CHANGE PASSWORD</div>
          <div style={{ color: '#FF6B6B !important', padding: '20px !important', textAlign: 'center', fontFamily: 'PT Sans', fontSize: '1em' }}>Cannot change password</div>
        </>
      );
    }

    if (sending) {
      return this.changePasswordForm();
    }

    if (success) {
      const { redirectCountdown } = this.state;
      return (
        <>
          <div className="maintitle" style={{ fontFamily: 'Oswald !important', letterSpacing: '0.05em !important', fontSize: '1.125em !important', fontWeight: '500 !important' }}>CHANGE PASSWORD</div>
          <div id="success-message" style={{ padding: '20px !important', textAlign: 'center', fontFamily: 'PT Sans', fontSize: '1em' }}>Your password has been changed</div>
          <div style={{ color: '#938f8f !important', padding: '0px 20px !important', textAlign: 'center', fontFamily: 'PT Sans', fontSize: '0.7em', marginTop: '20px' }}>
            Redirecting to login page in {redirectCountdown} seconds...
          </div>
        </>
      );
    }

    return this.changePasswordForm();
  }

  changePasswordForm = () => {
    const { oldPassword, newPassword, newPasswordConfirmation, showOldPassword, showNewPassword, showConfirmPassword } = this.state;

    const inputStyles = {
      fontFamily: 'PT Sans !important',
      fontSize: '1.025em !important',
      color: '#0A3C5D !important',
      fontWeight: '400 !important',
      letterSpacing: '0.03 !important',
      '& .MuiInput-input::placeholder': {
        color: 'rgb(130, 130, 130) !important',
        opacity: '1 !important',
        fontWeight: '400 !important',
        lineHeight: '16px !important',
      },
      '& .MuiInput-underline:before': {
        borderBottomColor: '#dadada !important',
      },
      '& .MuiInput-underline:hover:before': {
        borderBottomColor: '#dadada !important',
      },
      '& .MuiInput-underline:after': {
        borderBottomColor: '#003B5C !important',
      },
    };

    const iconButtonStyles = {
      color: '#00000061 !important',
      padding: '4px 8px !important',
      minWidth: 'auto !important',
      '&:hover': {
        backgroundColor: 'transparent !important',
      },
    };

    return (
      <>
        <div className="maintitle" style={{ fontFamily: 'Oswald !important', letterSpacing: '0.05em !important', fontSize: '1.125em !important', fontWeight: '500 !important' }}>CHANGE PASSWORD</div>
        {this.emptyFieldValidation()}
        {this.passwordNotMatch()}
        <Stack spacing={2}>
          <FormControl fullWidth>
          <div className="input-label">Old password:</div>
          <TextField
            id="oldpassword"
            type={showOldPassword ? 'text' : 'password'}
            placeholder="Old password"
            variant='standard'
            inputRef={this.oldPasswordInputRef}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => this.setOldPassword(event.target.value)}
            value={oldPassword}
            defaultValue={oldPassword}
            sx={inputStyles}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ marginRight: '0px !important' }}>
                  <IconButton
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={this.toggleOldPasswordVisibility}
                    edge="end"
                    sx={iconButtonStyles}
                    tabIndex={-1}
                    aria-label={showOldPassword ? 'Hide old password' : 'Show old password'}
                  >
                    <i className="material-icons" style={{ fontSize: '20px', lineHeight: '20px' }}>
                      {showOldPassword ? 'remove_red_eye' : 'visibility_off'}
                    </i>
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          </FormControl>

          <FormControl fullWidth>
          <div className="input-label">Password:</div>
          <TextField
            id="newpassword"
            variant='standard'
            type={showNewPassword ? 'text' : 'password'}
            placeholder="New password"
            value={newPassword}
            inputRef={this.newPasswordInputRef}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => this.setNewPassword(event.target.value)}
            sx={inputStyles}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ marginRight: '0px !important' }}>
                  <IconButton
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={this.toggleNewPasswordVisibility}
                    edge="end"
                    sx={iconButtonStyles}
                    tabIndex={-1}
                    aria-label={showNewPassword ? 'Hide new password' : 'Show new password'}
                  >
                    <i className="material-icons" style={{ fontSize: '20px', lineHeight: '20px' }}>
                      {showNewPassword ? 'remove_red_eye' : 'visibility_off'}
                    </i>
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          </FormControl>

          <FormControl fullWidth>
          <div className="input-label">Confirm password:</div>
          <TextField
            id="newpasswordconfirmation"
            type={showConfirmPassword ? 'text' : 'password'}
            variant='standard'
            placeholder="New password confirmation"
            value={newPasswordConfirmation}
            inputRef={this.confirmPasswordInputRef}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => this.setNewPasswordConfirmation(event.target.value)}
            sx={inputStyles}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ marginRight: '0px !important' }}>
                  <IconButton
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={this.toggleConfirmPasswordVisibility}
                    edge="end"
                    sx={iconButtonStyles}
                    tabIndex={-1}
                    aria-label={showConfirmPassword ? 'Hide password confirmation' : 'Show password confirmation'}
                  >
                    <i className="material-icons" style={{ fontSize: '20px', lineHeight: '20px' }}>
                      {showConfirmPassword ? 'remove_red_eye' : 'visibility_off'}
                    </i>
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          </FormControl>
          <Button
            variant="text"
            type="button"
            className="form_button"
            id="change_password_button"
            onClick={this.changePassword}
            sx={{
              display: 'block !important',
              fontFamily: 'Oswald !important',
              letterSpacing: '0.05em !important',
              fontSize: '14pem !important',
              fontWeight: '500 !important',
              color: '#CF5232 !important',
              paddingLeft: '0px !important',
              textAlign: 'left !important',
              marginTop: '10px !important'
            }}
          >
            CHANGE PASSWORD
          </Button>
        </Stack>
      </>
    );
  }

  render() {
    return (
      <div className="app-content change-password">
        <Header />
        <HeaderImage content={this.content()} />
        <Footer />
      </div>
    );
  }
}

function mapStateToProps(appState: appState) {
  return {
    changePassword: appState.changePassword,
  };
}

export default connect(mapStateToProps)(ChangePassword);
